import { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";

const emptyForm = { title: "", caption: "", link_url: "", display_order: 0 };

function SlidersPage() {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      const res = await axiosInstance.get("/api/sliders");
      setSliders(res.data);
    } catch (err) {
      toast.error("Failed to load sliders");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setImageFile(null);
    setShowModal(true);
  };

  const openEditModal = (slider) => {
    setEditingId(slider.id);
    setForm({
      title: slider.title || "",
      caption: slider.caption || "",
      link_url: slider.link_url || "",
      display_order: slider.display_order,
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingId && !imageFile) {
      toast.error("Please select an image");
      return;
    }

    setSaving(true);

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("caption", form.caption);
    formData.append("link_url", form.link_url);
    formData.append("display_order", form.display_order);
    if (imageFile) formData.append("image", imageFile);

    try {
      if (editingId) {
        await axiosInstance.put(`/api/sliders/${editingId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Slider updated");
      } else {
        await axiosInstance.post("/api/sliders", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Slider added");
      }
      setShowModal(false);
      fetchSliders();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save slider");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete slider "${title || "Untitled"}"?`)) return;
    try {
      await axiosInstance.delete(`/api/sliders/${id}`);
      toast.success("Slider deleted");
      fetchSliders();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Homepage Sliders</h4>
          <p className="text-muted small mb-0">
            Shown in order on the homepage carousel. Lower number = shown first.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openAddModal}>
          <i className="bi bi-plus-lg me-1"></i> Add Slide
        </button>
      </div>

      <div className="row g-3">
        {sliders.length === 0 ? (
          <p className="text-muted">No slides added yet.</p>
        ) : (
          sliders
            .slice()
            .sort((a, b) => a.display_order - b.display_order)
            .map((slider) => (
              <div className="col-12 col-md-6 col-lg-4" key={slider.id}>
                <div className="card border-0 shadow-sm h-100">
                  <div
                    style={{
                      height: "160px",
                      overflow: "hidden",
                      background: "#f0f0f0",
                    }}
                  >
                    <img
                      src={`${process.env.REACT_APP_API_URL}/${slider.image_path}`}
                      alt={slider.title || "Slide"}
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                  </div>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <h6 className="mb-0">
                        {slider.title || (
                          <span className="text-muted">Untitled</span>
                        )}
                      </h6>
                      <span className="badge bg-light text-dark border">
                        #{slider.display_order}
                      </span>
                    </div>
                    <p className="text-muted small mb-2">
                      {slider.caption || "—"}
                    </p>
                    {slider.link_url && (
                      <p className="small mb-2">
                        <i className="bi bi-link-45deg me-1"></i>
                        <span
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: "100%" }}
                        >
                          {slider.link_url}
                        </span>
                      </p>
                    )}
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => openEditModal(slider)}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(slider.id, slider.title)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
        )}
      </div>

      {showModal && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingId ? "Edit Slide" : "Add Slide"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowModal(false)}
                    ></button>
                  </div>

                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={form.title}
                        onChange={handleFormChange}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Caption</label>
                      <input
                        type="text"
                        className="form-control"
                        name="caption"
                        value={form.caption}
                        onChange={handleFormChange}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Link URL{" "}
                        <small className="text-muted">
                          (optional — where clicking the slide goes)
                        </small>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="link_url"
                        placeholder="/contact or https://..."
                        value={form.link_url}
                        onChange={handleFormChange}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="display_order"
                        value={form.display_order}
                        onChange={handleFormChange}
                      />
                    </div>

                    <div className="mb-1">
                      <label className="form-label">Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={(e) => setImageFile(e.target.files[0])}
                      />
                      {editingId && (
                        <small className="text-muted">
                          Leave empty to keep the current image
                        </small>
                      )}
                      <small className="d-block text-muted mt-1">
                        Recommended: 1600×600px or similar wide banner size
                      </small>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? "Saving..." : editingId ? "Update" : "Add"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}
    </div>
  );
}

export default SlidersPage;
